/**
 * Lab setups — an arrangement on a virtual lab that a teacher has saved.
 *
 * See docs/FeatureDesigns/lab-setups/lab-setups-target.md.
 *
 * A saved setup is the same thing a preset or a guided experiment is — a
 * starting arrangement on a lab — written by a teacher instead of by us. The
 * three share this vocabulary and the way a lab opens on one; they do not
 * share storage. Presets live in code (their tests hold them to the book),
 * guided experiments in the curated catalogue, and saved setups in their own
 * per-owner container.
 *
 * The envelope knows nothing about any lab. The `payload` is the lab's own,
 * versioned, and checked by the lab's adapter (`registry.ts`).
 */

/** Which lab a setup opens on. Grows one id per lab that learns to save. */
export type LabId = 'optics-bench' | 'mechanics-bench' | 'chemistry-lab';

export const LAB_IDS: readonly LabId[] = ['optics-bench', 'mechanics-bench', 'chemistry-lab'];

export const isLabId = (v: unknown): v is LabId => typeof v === 'string' && (LAB_IDS as readonly string[]).includes(v);

/** What a setup was started from, so its card can say so. */
export interface LabSetupBasis {
    /** A preset's key, when it was saved from one. */
    preset?: string;
    /** Another saved setup's id, when it was saved as a copy of it. */
    setupId?: string;
}

/** What a client sends to create a setup. */
export interface LabSetupInput<P = unknown> {
    labId: LabId;
    /** Which bench inside the lab: 'quantitative' for the chemistry bench with
     *  amounts. Absent for a lab that is one bench. */
    labRef?: string;
    name: string;
    /** The teacher's own words about it — shown where a preset shows its claim. */
    notes?: string;
    chapterIds?: string[];
    basedOn?: LabSetupBasis;
    payloadVersion: number;
    payload: P;
}

/** What a client sends to change one. Every field is optional; the lab is not. */
export type LabSetupPatch<P = unknown> = Partial<Pick<LabSetupInput<P>, 'name' | 'notes' | 'chapterIds' | 'payloadVersion' | 'payload'>>;

/** A setup as stored in the `lab-setups` container, partitioned by `ownerId`. */
export interface LabSetupDoc<P = unknown> extends LabSetupInput<P> {
    id: string;
    docType: 'lab-setup';
    ownerId: string;
    ownerRole: 'teacher';
    tenantId?: string;
    /** One line for the card, worked out from the payload by the lab's adapter
     *  on every write — so a list never has to read the payloads. */
    summary: string;
    /** Stays 'private'. Who else may open a setup is held in share rows, not
     *  here — docs/FeatureDesigns/sharing/sharing-target.md. */
    visibility: 'private';
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
}

/** A setup as a list returns it: everything but the payload. */
export type LabSetupSummary = Omit<LabSetupDoc, 'payload'>;

/** Who shared a setup with the reader, and how (sharing-target §6.1, SH-23). */
export interface LabSetupSharedBy {
    shareId: string;
    ownerId: string;
    ownerName: string;
    level: 'view' | 'use' | 'copy' | 'edit';
    sharedAt: string;
}

/** A setup someone else shared with the reader: never with the owner's notes. */
export type SharedLabSetupSummary = Omit<LabSetupSummary, 'notes'> & { sharedBy: LabSetupSharedBy };

export interface LabSetupListResponse {
    /** The caller's own. Empty for someone who cannot save setups. */
    setups: LabSetupSummary[];
    /** Asked for with `include=shared`: what others shared with the caller. */
    shared?: SharedLabSetupSummary[];
}
export interface LabSetupResponse<P = unknown> {
    /** For a shared setup, without the owner's notes. */
    setup: LabSetupDoc<P>;
    /** Present when the caller is not the owner and reads it through a share. */
    sharedBy?: LabSetupSharedBy;
}

/** The most setups one teacher keeps. A guard against a runaway client, not a plan limit. */
export const LAB_SETUPS_PER_OWNER = 500;
export const LAB_SETUP_NAME_MAX = 120;
export const LAB_SETUP_NOTES_MAX = 2000;

/** What each lab is called where a setup is listed away from its bench. */
export const LAB_LABELS: Record<LabId, string> = {
    'optics-bench': 'Optics bench',
    'mechanics-bench': 'Mechanics bench',
    'chemistry-lab': 'Chemistry lab',
};

/**
 * Where, in the student app, a setup shared with the reader opens: its lab's
 * bench (for chemistry, the bench it was made on), told which setup with
 * `?shared=`. The bench reads it through the share; the address opens nothing
 * by itself (sharing-target §7).
 */
export function sharedSetupPath(setup: Pick<LabSetupDoc, 'id' | 'labId' | 'labRef'>): string {
    const base = setup.labId === 'optics-bench' ? '/optics-bench'
        : setup.labId === 'mechanics-bench' ? '/mechanics-bench'
            : setup.labRef === 'quantitative' ? '/chemistry-lab/quantitative' : '/chemistry-lab';
    return `${base}?shared=${encodeURIComponent(setup.id)}`;
}
